'use client';

import { Xmark } from '@byway/icons';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import pluralize from 'pluralize';
import { type ReactNode, useRef, useState } from 'react';
import type { MapFocus, MapMode } from '~/components/map';
import { BywayMapLazy } from '~/components/map/BywayMapLazy';
import mapStyles from '~/components/map/Map.module.css';
import { Spinner } from '~/components/Spinner';
import { TRPC_SETTINGS } from '~/constants/trpc';
import { api } from '~/libs/api';
import type { TripAccommodationStay, TripOverviewStay } from '~/services/engine-ts';
import {
  AccommodationStaysMapScroller,
  type AccommodationStaysMapScrollerHandle
} from './AccommodationStaysMapScroller';
import sheetStyles from './AccommodationStaysMapSheet.module.css';
import { tripStaysToMapData } from './tripStaysToMapData';

type AccommodationStaysMapSheetProps = {
  onClose: () => void;
  tripId: string;
};

const parseStayMarkerId = (id: string): number | null => {
  if (!id.startsWith('stay-')) return null;
  const itemId = Number.parseInt(id.slice(5), 10);
  return Number.isFinite(itemId) ? itemId : null;
};

const buildStayTooltip = (stay: TripAccommodationStay | undefined, placeName: string) => {
  if (!stay) return <strong className={mapStyles.popupHeading}>{placeName}</strong>;
  return (
    <>
      <strong className={mapStyles.popupHeading}>{stay.accommodation.title}</strong>
      <div className={mapStyles.popupStayMeta}>{pluralize('night', stay.nights, true)}</div>
      <div className={mapStyles.popupStayMeta}>{stay.placeName}</div>
    </>
  );
};

const MapSheetShell = ({
  onClose,
  closeLabel,
  children
}: Readonly<{
  onClose: () => void;
  closeLabel: string;
  children: ReactNode;
}>) => (
  <div className={clsx(mapStyles.root, mapStyles.rootOverlay, sheetStyles.shell)}>
    <button
      type="button"
      className={clsx(mapStyles.overlayButton, mapStyles.closeButton)}
      onClick={onClose}
      aria-label={closeLabel}
      data-test-id="accommodation-map-close"
    >
      <Xmark decorative size="small" />
    </button>
    {children}
  </div>
);

export const AccommodationStaysMapSheet = ({ onClose, tripId }: Readonly<AccommodationStaysMapSheetProps>) => {
  const t = useTranslations('journey');
  const tAria = useTranslations('aria');
  const [mode, setMode] = useState<MapMode>('expanded');
  const [focus, setFocus] = useState<MapFocus>(null);
  const scrollerRef = useRef<AccommodationStaysMapScrollerHandle | null>(null);

  const { data: overview, isLoading: isOverviewLoading } = api.trip.getOverview.useQuery(
    { tripId },
    { ...TRPC_SETTINGS.QUERY_NO_REFETCH, enabled: !!tripId, keepPreviousData: true }
  );

  const { data: accommodation, isLoading: isAccommodationLoading } = api.trip.getAccommodation.useQuery(
    { tripId },
    { ...TRPC_SETTINGS.QUERY_NO_REFETCH, enabled: !!tripId, keepPreviousData: true }
  );

  const overviewStays = overview?.items.filter((item): item is TripOverviewStay => item.type === 'stay') ?? [];

  const mapData = tripStaysToMapData(overview?.items ?? []);

  const staysByItemId = new Map((accommodation?.stays ?? []).map((stay) => [stay.itemId, stay] as const));

  const activeItemId = focus?.kind === 'marker' ? parseStayMarkerId(focus.id) : null;

  const handleModeChange = (next: MapMode) => {
    setMode(next);
    if (next === 'compact') onClose();
  };

  const handleStaySelect = (itemId: number) => {
    setFocus({ kind: 'marker', id: `stay-${itemId}` });
  };

  const handleFocusChange = (next: MapFocus) => {
    setFocus(next);
    if (next?.kind === 'marker') {
      const itemId = parseStayMarkerId(next.id);
      if (itemId !== null) scrollerRef.current?.scrollToStay(itemId);
    }
  };

  const closeLabel = tAria('close');

  if (isOverviewLoading || isAccommodationLoading) {
    return (
      <MapSheetShell onClose={onClose} closeLabel={closeLabel}>
        <div className={sheetStyles.shellMessage} aria-busy="true" aria-live="polite">
          <Spinner />
        </div>
      </MapSheetShell>
    );
  }

  if (mapData.markers.length === 0) {
    return (
      <MapSheetShell onClose={onClose} closeLabel={closeLabel}>
        <p className={sheetStyles.shellMessage}>{t('accommodation_map_unavailable')}</p>
      </MapSheetShell>
    );
  }

  const markersWithTooltips = mapData.markers.map((marker) => {
    const itemId = parseStayMarkerId(marker.id);
    const stay = itemId !== null ? staysByItemId.get(itemId) : undefined;
    const overviewStay = itemId !== null ? overviewStays.find((s) => s.itemId === itemId) : undefined;
    return {
      ...marker,
      tooltip: buildStayTooltip(stay, overviewStay?.place.name ?? marker.label ?? '')
    };
  });

  return (
    <BywayMapLazy
      markers={markersWithTooltips}
      segments={mapData.segments}
      route={mapData.route}
      mode={mode}
      onModeChange={handleModeChange}
      focus={focus}
      onFocusChange={handleFocusChange}
      showLabels
      showDetailedViewButton={false}
      overlayBottomSlot={
        accommodation?.stays.length ? (
          <AccommodationStaysMapScroller
            ref={scrollerRef}
            stays={accommodation.stays}
            activeItemId={activeItemId}
            onStaySelect={handleStaySelect}
          />
        ) : null
      }
    />
  );
};
